import { css } from "@emotion/css";
import { useRef } from "react";
import { AriaButtonProps, useButton } from "react-aria";

import { DropdownMenu } from "../../dropdown-menu/DropdownMenu";
import { MenuItem } from "../../dropdown-menu/assets/MenuItem";
import { Text } from "../../../foundations/typography/Text";
import { useColors } from "../../../hooks/useColors";

import { Icon } from "../../../foundations/icons/Icon/Icon";
import { ActionListItem } from "../../action-list/ActionListItem";
import { useGetAllFocusShadows, useMediaQuery } from "../../utils";

interface AccountDropdownProps extends AriaButtonProps {
  title: string;
  children: React.ReactNode;
}

export function AccountDropdown(props: AccountDropdownProps) {
  const colors = useColors();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { mediaQuery } = useMediaQuery();
  const getAllFocusShadows = useGetAllFocusShadows();
  const { buttonProps } = useButton({ ...props }, triggerRef);

  return (
    <DropdownMenu
      placement="left"
      headerTitle="Dunder Mifflin"
      headerText="dundermifflin.certn.co"
      minWidth="290px"
      triggerButton={
        <button
          className={css(
            styles.button,
            css(
              {
                border: `1px solid ${colors["border-base"]}`,
                "&:hover": {
                  backgroundColor: "var(--background-selected-weak-hover)",
                },
                "&:focus-within": getAllFocusShadows(),
              },
              mediaQuery({
                width: ["100%", undefined],
              })
            )
          )}
          {...buttonProps}
          ref={triggerRef}
        >
          <ActionListItem
            title={props.title}
            alignItems="center"
            padding="var(--space-3)"
            trailing={<Icon name="expand_more" />}
          >
            {props.children}
          </ActionListItem>
        </button>
      }
    >
      <MenuItem icon="person_add" iconColor="icon-weak">
        <Text style="input/text-field">Add user</Text>
      </MenuItem>
      <MenuItem icon="add" iconColor="icon-weak">
        <Text style="input/text-field">Add team</Text>
      </MenuItem>
      <MenuItem icon="settings" iconColor="icon-weak">
        <Text style="input/text-field">Company settings</Text>
      </MenuItem>
    </DropdownMenu>
  );
}

const styles = {
  button: css({
    all: "unset",
    cursor: "pointer",
    display: "flex",
    textAlign: "start",
    boxSizing: "border-box",
    borderRadius: "var(--border-radius-3)",
  }),
};
